<template>
    <div class="flex flex-col gap-4">

        <!-- HEADER -->
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
            Search and manage users
        </p>

        <!-- MESSAGES -->
        <div v-if="successMessage" class="text-sm text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-md">
            {{ successMessage }}
        </div>

        <div v-if="errorMessage" class="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-md">
            {{ errorMessage }}
        </div>

        <!-- TOOLBAR -->
        <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">

            <div class="flex gap-2 flex-1">
                <input v-model="userSearch" type="text" placeholder="Search user..." class="flex-1 px-3 py-2 text-sm rounded-md border bg-white dark:bg-gray-900
                    border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#E87055]" />

                <button @click="searchUser" :disabled="isLoading"
                    class="px-4 py-2 text-sm rounded-md bg-[#E87055] text-white disabled:opacity-50">
                    {{ isLoading ? 'Searching...' : 'Search' }}
                </button>
            </div>

            <button @click="showCreateForm = true"
                class="px-4 py-2 text-sm rounded-md bg-green-600 hover:bg-green-700 text-white">
                + Add User
            </button>
        </div>

        <!-- USER INFO LABEL -->
        <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">
            User Info
        </p>

        <!-- USER INFO / SELECTED USER -->
        <div v-if="selectedUser" class="border rounded-lg p-4 bg-white dark:bg-gray-900 shadow-sm space-y-4">

            <!-- HEADER -->
            <div class="flex items-start justify-between">
                <div>
                    <p class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {{ selectedUser.username }}
                    </p>
                    <p class="text-xs text-gray-500">
                        {{ selectedUser.name }}
                    </p>
                </div>

                <span class="text-xs px-2 py-1 rounded-full bg-[#E87055]/10 text-[#E87055]">
                    {{ selectedUser.roles }}
                </span>
            </div>

            <!-- ROLE UPDATE -->
            <div>
                <label class="text-xs text-gray-500">Change Role</label>

                <select v-model="selectedUser.roles"
                    class="w-full mt-1 px-3 py-2 text-sm rounded-md border dark:bg-gray-800 dark:border-gray-700">
                    <option value="user">User</option>
                    <option value="viewing">Viewing</option>
                    <option value="admin">Admin</option>
                    <option value="super">Super</option>
                </select>

                <!-- RIGHT ALIGNED SAVE -->
                <div class="flex justify-end mt-2">
                    <button @click="updateRole"
                        class="px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md">
                        Save Role
                    </button>
                </div>
            </div>

            <!-- ACTIONS -->
            <div class="border-t pt-3 flex flex-wrap gap-2">

                <button @click="openChangePassword"
                    class="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md">
                    Change Password
                </button>

                <button @click="openResetConfirm"
                    class="px-3 py-2 text-sm bg-yellow-500 hover:bg-yellow-600 text-white rounded-md">
                    Reset Password
                </button>

            </div>
        </div>

        <!-- FALLBACK -->
        <div v-else class="border rounded-md p-4 text-sm text-gray-500 bg-gray-50 dark:bg-gray-800">
            No user selected.
        </div>

        <!-- USERS LIST LABEL -->
        <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Users List
        </p>

        <!-- USER LIST -->
        <div v-if="users.length"
            class="border rounded-md divide-y border-gray-200 dark:border-gray-700 max-h-72 overflow-y-auto">

            <div v-for="u in users" :key="u.id" @click="selectUser(u)"
                class="p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition">

                <div class="flex items-center justify-between">
                    <div class="font-medium text-sm text-gray-800 dark:text-gray-100">
                        {{ u.username }}
                    </div>

                    <span
                        class="text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {{ u.roles }}
                    </span>
                </div>

                <div class="text-xs text-gray-500">
                    {{ u.name }}
                </div>
            </div>
        </div>

        <div v-if="userSearch && !users.length && !isLoading" class="text-xs text-gray-500 italic">
            No user found.
        </div>

        <!-- CREATE USER MODAL -->
        <div v-if="showCreateForm" class="fixed inset-0 z-50 flex items-center justify-center">
            <div class="absolute inset-0 bg-black/60" @click="showCreateForm = false"></div>

            <div class="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 space-y-4">

                <div>
                    <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        Create User
                    </h3>
                    <p class="text-xs text-gray-500">Add a new system user account</p>
                </div>

                <div class="space-y-3">

                    <input v-model="newUser.username" placeholder="Username"
                        class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800" />

                    <input v-model="newUser.name" placeholder="Name"
                        class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800" />

                    <input v-model="newUser.password" type="password" placeholder="Password"
                        class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800" />

                    <select v-model="newUser.roles"
                        class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800">
                        <option value="user">User</option>
                        <option value="viewing">Viewing</option>
                        <option value="admin">Admin</option>
                        <option value="super">Super</option>
                    </select>

                </div>

                <div class="flex justify-end gap-2 pt-2 border-t dark:border-gray-700">

                    <button @click="showCreateForm = false"
                        class="px-4 py-2 text-sm rounded-md bg-gray-500 hover:bg-gray-600 text-white">
                        Cancel
                    </button>

                    <button @click="createUser" :disabled="isCreating"
                        class="px-4 py-2 text-sm rounded-md bg-green-600 hover:bg-green-700 text-white">
                        {{ isCreating ? 'Creating...' : 'Create User' }}
                    </button>

                </div>

            </div>
        </div>


        <!-- RESET CONFIRM MODAL -->
        <div v-if="showResetConfirm" class="fixed inset-0 z-50 flex items-center justify-center">
            <div class="absolute inset-0 bg-black/60" @click="showResetConfirm = false"></div>

            <div class="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 space-y-4">

                <div>
                    <h3 class="text-lg font-semibold text-red-600">
                        Reset Password
                    </h3>
                    <p class="text-xs text-gray-500">
                        Type <b>reset {{ selectedUser?.username }}</b> to confirm
                    </p>
                </div>

                <input v-model="resetConfirmInput"
                    class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800"
                    placeholder="confirmation text..." />

                <div class="flex justify-end gap-2 pt-2 border-t dark:border-gray-700">

                    <button @click="showResetConfirm = false"
                        class="px-4 py-2 text-sm rounded-md bg-gray-500 hover:bg-gray-600 text-white">
                        Cancel
                    </button>

                    <button @click="resetPassword"
                        :disabled="isResetting || resetConfirmInput !== `reset ${selectedUser?.username}`"
                        class="px-4 py-2 text-sm rounded-md bg-yellow-600 hover:bg-yellow-700 text-white">
                        {{ isResetting ? 'Resetting...' : 'Confirm Reset' }}
                    </button>

                </div>

            </div>
        </div>


        <!-- CHANGE PASSWORD MODAL -->
        <div v-if="showChangePassword" class="fixed inset-0 z-50 flex items-center justify-center">

            <div class="absolute inset-0 bg-black/60" @click="showChangePassword = false"></div>

            <div class="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 space-y-4">

                <div>
                    <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        Change Password
                    </h3>
                    <p class="text-xs text-gray-500">
                        Update credentials for selected user
                    </p>
                </div>

                <div class="space-y-3">

                    <input v-model="passwordForm.currentPassword" type="password" placeholder="Current Password"
                        class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800" />

                    <input v-model="passwordForm.newPassword" type="password" placeholder="New Password"
                        class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800" />

                    <input v-model="passwordForm.confirmPassword" type="password" placeholder="Confirm Password"
                        class="w-full px-3 py-2 text-sm rounded-md border dark:border-gray-700 dark:bg-gray-800" />

                </div>

                <div class="flex justify-end gap-2 pt-2 border-t dark:border-gray-700">

                    <button @click="showChangePassword = false"
                        class="px-4 py-2 text-sm rounded-md bg-gray-500 hover:bg-gray-600 text-white">
                        Cancel
                    </button>

                    <button @click="changePassword" :disabled="isChangingPassword"
                        class="px-4 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white">
                        {{ isChangingPassword ? 'Updating...' : 'Update Password' }}
                    </button>

                </div>

            </div>
        </div>

    </div>
</template>

<script>
import { ref, inject, onMounted } from "vue";

export default {
    setup() {
        const axios = inject("$axios");

        const userSearch = ref("");
        const users = ref([]);
        const selectedUser = ref(null);

        const isLoading = ref(false);
        const isResetting = ref(false);
        const isCreating = ref(false);
        const isChangingPassword = ref(false);

        const showCreateForm = ref(false);
        const showChangePassword = ref(false);
        const showResetConfirm = ref(false);

        const resetConfirmInput = ref("");

        const newUser = ref({
            username: "",
            name: "",
            password: "",
            roles: "user"
        });

        const passwordForm = ref({
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        });

        const successMessage = ref("");
        const errorMessage = ref("");

        const clearMessages = () => {
            successMessage.value = "";
            errorMessage.value = "";
        };

        const fetchUsers = async () => {
            try {
                isLoading.value = true;
                const res = await axios.put("/api/user/get");
                users.value = res.data?.data || [];
            } catch {
                errorMessage.value = "Failed to load users.";
            } finally {
                isLoading.value = false;
            }
        };

        const searchUser = async () => {
            clearMessages();

            selectedUser.value = null

            if (!userSearch.value) return fetchUsers();

            try {
                isLoading.value = true;

                const res = await axios.put(
                    `/api/user/get?query=${encodeURIComponent(userSearch.value)}`
                );

                users.value = res.data?.data || [];
            } catch {
                errorMessage.value = "Search failed.";
            } finally {
                isLoading.value = false;
            }
        };

        const selectUser = (u) => {
            selectedUser.value = JSON.parse(JSON.stringify(u));
        };

        const createUser = async () => {
            try {
                isCreating.value = true;

                await axios.post("/api/user/register", newUser.value);

                successMessage.value = "User created successfully.";

                showCreateForm.value = false;

                newUser.value = {
                    username: "",
                    name: "",
                    password: "",
                    roles: "user"
                };

                await fetchUsers();

            } catch {
                errorMessage.value = "Create failed.";
            } finally {
                isCreating.value = false;
            }
        };

        const openResetConfirm = () => {
            resetConfirmInput.value = "";
            showResetConfirm.value = true;
        };

        const resetPassword = async () => {
            if (resetConfirmInput.value !== `reset ${selectedUser.value.username}`) return;

            try {
                isResetting.value = true;

                await axios.put("/api/user/reset-password", {
                    userId: selectedUser.value.id
                });

                successMessage.value = "Password reset successfully.";
                showResetConfirm.value = false;

            } catch {
                errorMessage.value = "Reset failed.";
            } finally {
                isResetting.value = false;
            }
        };

        const openChangePassword = () => {
            passwordForm.value = {
                currentPassword: "",
                newPassword: "",
                confirmPassword: ""
            };
            showChangePassword.value = true;
        };

        const changePassword = async () => {
            if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
                errorMessage.value = "Passwords do not match.";
                return;
            }

            try {
                isChangingPassword.value = true;

                await axios.put("/api/user/change-password", {
                    userId: selectedUser.value.id,
                    currentPassword: passwordForm.value.currentPassword,
                    newPassword: passwordForm.value.newPassword
                });

                successMessage.value = "Password updated.";
                showChangePassword.value = false;

            } catch {
                errorMessage.value = "Change failed.";
            } finally {
                isChangingPassword.value = false;
            }
        };

        const updateRole = async () => {
            try {
                await axios.patch("/api/user/role", {
                    userId: selectedUser.value.id,
                    roles: selectedUser.value.roles
                });

                successMessage.value = "Role updated successfully.";
                await fetchUsers();

            } catch {
                errorMessage.value = "Role update failed.";
            }
        };

        onMounted(fetchUsers);

        return {
            userSearch,
            users,
            selectedUser,

            isLoading,
            isResetting,
            isCreating,
            isChangingPassword,

            showCreateForm,
            showChangePassword,
            showResetConfirm,

            newUser,
            passwordForm,
            resetConfirmInput,

            successMessage,
            errorMessage,

            searchUser,
            selectUser,
            createUser,
            resetPassword,
            openChangePassword,
            openResetConfirm,
            changePassword,
            updateRole
        };
    }
};
</script>